import React, { useState } from "react";
import Page from "../Page.js";
import PageContentWrapper from "../../components/PageContentWrapper.js";
import uiManager from "../../../config/ui.js";
import PAGES from "../../data/PagesConstants.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faCloudUpload, faFileCircleCheck } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { getApi } from '../../api/endpoints.js';
import { toast } from 'react-toastify';
import FormPartial from "./FormPartial.js";
import PincodeModel from '../../models/Pincode.js';

const COLUMN_NAMES = ["code", "area", "city", "dist", "state", 'cluster_id']; //Acceptable column names.

const New = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [fileTypeError, setFileTypeError] = useState(null);
  const [fileMessage, setFileMessage] = useState("");
  const [fileUploaded, setFileUploaded] = useState(false);
  const [createApi] = getApi('createPincode');
  const navigate = useNavigate();

  const [pincodeModel, setPincodeModel] = useState(new PincodeModel());

  const createRecord = async (params) => {
    try {
      await createApi(params).unwrap();
      toast.success("Created Successfully");
      navigate(-1);
    } catch (error) {
      let pincodeModelObj = new PincodeModel(pincodeModel);
      pincodeModelObj.set_api_errors(error?.data?.data?.errors);
      toast.error(error?.data?.message || 'Something went wrong !');
    }
  }

  const onFormSubmit = () => {
    try{
      createRecord(new PincodeModel(pincodeModel).toApiJson());
    } catch (e) {
      setPincodeModel(new PincodeModel(pincodeModel));
    }
  }

  const handleCancelFunction = () => {
    navigate(-1);
  }

  const handleFile = (e) => {
    let fileTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
    let selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile && fileTypes.includes(selectedFile.type)) {
        const fileSize = selectedFile.size / (1024 * 1024);
        if (fileSize < 5) {
          setFileTypeError(null);
          const fileName = e.target.value.split('\\').pop();
          setFileMessage(fileName);
          let reader = new FileReader();
          reader.readAsArrayBuffer(selectedFile);
          reader.onload = (e) => {
            setExcelFile(e.target.result);
            setFileUploaded(false);
            const workbook = XLSX.read(e.target.result, { type: 'buffer' });
            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];
            const data = XLSX.utils.sheet_to_json(worksheet);
            setExcelData(filterColumns(data.slice(0, 10)));
          }
        } else {
          setFileTypeError('Please select a file that is 5 MB or smaller.');
          setExcelFile(null);
          setExcelData(null);
          setFileUploaded(false);
        }
      } else {
        setFileTypeError('Please select only excel file types.');
        setExcelFile(null);
        setExcelData(null);
        setFileUploaded(false);
      }
    } else {
      setFileMessage('No file selected');
      setFileUploaded(false);
    }
  }

  const filterColumns = (data) => {
    return data.map(row => {
      const filteredRow = {};
      COLUMN_NAMES.forEach(column => {
        if (row.hasOwnProperty(column)) {
          filteredRow[column] = row[column];
        }
      });
      return filteredRow;
    });
  }

  const handleFileSubmit = (e) => {
    if (excelFile !== null && excelData.length !== 0) {
      uploadFile({pincodes: excelData});
    } else {
      toast.error("Select file to upload")
    }
  }

  const uploadFile = async (params) => {
    try{
      await createApi(params).unwrap();
      toast.success("File Uploaded Successfully");
      navigate('/pincodes');
      setFileUploaded(true);
      setExcelData(null);
      setExcelFile(null);
      setFileMessage("");
    } catch (error){
      toast.error(error.data.message || "Something went wrong !");
    }
  }

  return <Page
    uiPage={uiManager.getUiPage(PAGES.NewPinCodePage.name)}
    breadcrumbs={[{ label: "Home", url: '/dashboard' }, { label: "PIN Codes List", url: '/pincodes' }, { label: "Add New PIN Code" }]}
    className={'new-pin-code-page'}
  >
    <PageContentWrapper contentHeading={"Add PIN Code"}>
      <>
        <p className="h6 text-center my-3">Add Single PIN Code</p>
      </>
      <div className="mt-4">
        <form className="row g-3" autoComplete="off">
          <FormPartial pincodeModel={pincodeModel} setPincodeModel={setPincodeModel} />
          <div className='mb-4 col-md-12'>
            <div >
              <button style={{ float: 'inline-start' }} type="button" className="btn btn-voltup-theme" onClick={handleCancelFunction}>Back</button>
              <button type="button" className="btn btn-voltup-theme ms-3" onClick={onFormSubmit}>Create</button>
            </div>
          </div>
        </form>
      </div>
      <>
        <p className="h4 text-center my-2 bg-warning-subtle">OR</p>
        <p className="h6 text-center my-3">Add Multiple PIN Codes With Bulk Upload</p>
      </>
      <>
        <div className="d-flex justify-content-center mt-5">
          <span><FontAwesomeIcon icon={faInfoCircle} className="me-1" /></span>
          Accepted file types are .xls or .xlsx and maximum file size should be 5 MB or less.
        </div>
        <form className="form-group files">
          <div className="file-drop-area">
            <div style={{ fontSize: '50px', opacity: '20%' }} className="text-center">
              <FontAwesomeIcon icon={excelFile ? faFileCircleCheck : faCloudUpload} />
            </div>
            <div className="text-center">
              <span className="choose-file-button">
                {excelFile ? "File Selected" : "Choose file"}
              </span>
              <span className="file-message">
                {excelFile ? fileMessage : "or drag and drop file here"}
              </span>
            </div>
            <input
              type="file"
              className="file-input"
              multiple={false}
              accept=".xls, .xlsx, .XLS, .XLSX, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ border: 'none' }}
              onChange={handleFile}
              required
            />
          </div>
          <div className="text-center opacity-50">{fileUploaded ? "File Uploaded!" : "No File is Uploaded yet!"}</div>
          <div className="d-flex justify-content-center mb-3">
            <button
              type="button"
              className="btn btn-sm btn-voltup-theme"
              onClick={handleFileSubmit}
            >
              Upload
            </button>
          </div>
          {fileTypeError && (
              <div className="alert alert-danger" role="alert">{fileTypeError}</div>
          )}
        </form>
        <React.Fragment>
          <div className="wrapper mx-3">
            <div className="viewer">
              {excelData ? (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        {COLUMN_NAMES.map((columnName, index) => (
                            <th key={index}>{columnName}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.map((individualExcelData, index) => (
                        <tr key={index}>
                          {COLUMN_NAMES.map((columnName, index) => (
                            <td key={index}>{individualExcelData[columnName]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <>
                  <div className="text-center pb-2 opacity-75">No File is selected yet, Please select the file!</div>
                  <div className="text-center fst-italic opacity-50 pb-2">Acceptable column names = "code", "area", "city", "dist", "state", 'cluster_id'</div>
                </>
              )}
            </div>
          </div>
        </React.Fragment>
      </>
    </PageContentWrapper>
  </Page>
}

export default New;
